import {
  buildStampLayerFabricObject,
  getStampFrameFromFabricJson,
} from '@/image-designer/utils/stampLayerUtils';
import {
  mergeExampleIntoLinkData,
  normalizeStampParams,
  stampExampleFromLinkData,
  stampParamsToExportFields,
  defaultStampAreaName,
} from '@/image-designer/utils/stampAreaConfig';
import { applyImagesToFabricJson, isIpeLayerName, normalizeIpeLayerName } from '@/image-designer/adapters/ipeLayerAdapter';
import { inlineUrlFromGoodsImageRef } from '@/pages/ipe/workbench/utils/goodsTemplateImageUrls';

/** 旧版套图编辑器展示容器尺寸（与 ImageTemplateEditorPage / goodsCanvasComposite 一致） */
export const STAMP_DISPLAY_CONTAINER = 900;

/**
 * object-fit: contain 下图片在容器内的实际显示区域
 */
export function fitImageInContainer(
  imageWidth,
  imageHeight,
  containerW = STAMP_DISPLAY_CONTAINER,
  containerH = STAMP_DISPLAY_CONTAINER,
) {
  if (!imageWidth || !imageHeight) {
    return {
      scaleX: 1,
      scaleY: 1,
      imageOffsetX: 0,
      imageOffsetY: 0,
      imageDisplayWidth: containerW,
      imageDisplayHeight: containerH,
    };
  }

  const imageAspect = imageWidth / imageHeight;
  const containerAspect = containerW / containerH;
  let scaleX;
  let scaleY;
  let imageDisplayWidth;
  let imageDisplayHeight;
  let imageOffsetX;
  let imageOffsetY;

  if (imageAspect > containerAspect) {
    scaleX = containerW / imageWidth;
    scaleY = scaleX;
    imageDisplayWidth = containerW;
    imageDisplayHeight = imageHeight * scaleY;
    imageOffsetX = 0;
    imageOffsetY = (containerH - imageDisplayHeight) / 2;
  } else {
    scaleY = containerH / imageHeight;
    scaleX = scaleY;
    imageDisplayHeight = containerH;
    imageDisplayWidth = imageWidth * scaleX;
    imageOffsetX = (containerW - imageDisplayWidth) / 2;
    imageOffsetY = 0;
  }

  return {
    scaleX,
    scaleY,
    imageOffsetX,
    imageOffsetY,
    imageDisplayWidth,
    imageDisplayHeight,
  };
}

/** 兼容历史 stampAreas 结构 */
function pickExampleExportFields(item) {
  const example = stampExampleFromLinkData(item);
  return Object.keys(example).length ? example : {};
}

export function normalizeStampAreas(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const normalized = {};
  Object.entries(raw).forEach(([imageId, value]) => {
    if (!imageId) return;
    let list = [];
    if (Array.isArray(value)) {
      list = value;
    } else if (value && typeof value === 'object') {
      list = Object.values(value);
    }
    normalized[imageId] = list
      .filter((item) => item && typeof item === 'object')
      .map((item, idx) => ({
        id: item.id || `stamp-${imageId}-${idx}`,
        name: item.name || defaultStampAreaName(idx + 1),
        x: Number(item.x) || 0,
        y: Number(item.y) || 0,
        width: Math.max(1, Number(item.width) || 0),
        height: Math.max(1, Number(item.height) || 0),
        rotation: Number(item.rotation) || 0,
        ...stampParamsToExportFields(item),
        ...pickExampleExportFields(item),
      }));
  });
  return normalized;
}

/**
 * 工作区像素坐标 → 旧编辑器 900 展示坐标
 */
export function workspaceBoundsToStampArea(bounds, workspaceSize) {
  const fit = fitImageInContainer(workspaceSize.width, workspaceSize.height);
  const ratio = fit.imageDisplayWidth / workspaceSize.width;
  return {
    x: bounds.left * ratio + fit.imageOffsetX,
    y: bounds.top * ratio + fit.imageOffsetY,
    width: bounds.width * ratio,
    height: bounds.height * ratio,
    rotation: bounds.angle || 0,
  };
}

/**
 * 旧编辑器展示坐标 → 工作区像素
 */
export function stampAreaToWorkspaceBounds(stamp, workspaceSize) {
  const fit = fitImageInContainer(workspaceSize.width, workspaceSize.height);
  const ratio = workspaceSize.width / fit.imageDisplayWidth;
  return {
    left: (stamp.x - fit.imageOffsetX) * ratio,
    top: (stamp.y - fit.imageOffsetY) * ratio,
    width: stamp.width * ratio,
    height: stamp.height * ratio,
    angle: stamp.rotation || 0,
  };
}

export function isStampFabricObject(obj) {
  return obj?.linkData?.role === 'stampArea' || obj?.stampRole === 'stampArea';
}

/** 合成 loadFromJSON 前需签名的印花 exampleImageUrl */
export function collectStampExampleUrlsFromFabricJson(fabricJson) {
  if (!fabricJson?.objects) return [];
  return fabricJson.objects
    .filter(isStampFabricObject)
    .map((obj) => String(obj.linkData?.exampleImageUrl || '').trim())
    .filter((url) => url && !url.startsWith('data:') && !url.startsWith('blob:'));
}

function resolveImageRefToUrl(rawRef, imageUrlMap = {}) {
  if (rawRef == null || rawRef === '') return null;
  if (typeof rawRef === 'object') {
    const id = String(rawRef.id || rawRef.imageId || rawRef.value || '').trim();
    // 优先用 map（可能已转成 data URL），再回落对象内联 URL
    if (id && imageUrlMap[id]) return imageUrlMap[id];
    const inline = inlineUrlFromGoodsImageRef(rawRef);
    if (inline) return inline;
    return null;
  }
  if (typeof rawRef === 'string' && rawRef.startsWith('data:image/')) return rawRef;
  if (typeof rawRef === 'string' && (rawRef.startsWith('http') || rawRef.startsWith('/') || rawRef.startsWith('blob:'))) {
    return rawRef;
  }
  const id = String(rawRef).trim();
  return imageUrlMap[id] || null;
}

function lookupImageGroupRef(imageGroup, stampName, objName) {
  const group = imageGroup && typeof imageGroup === 'object' ? imageGroup : {};
  const candidates = [stampName, objName].filter((k) => k != null && String(k).trim());
  for (const key of candidates) {
    if (group[key] != null && group[key] !== '') return group[key];
  }
  for (const key of candidates) {
    if (!isIpeLayerName(key)) continue;
    const normalized = normalizeIpeLayerName(key);
    if (group[normalized] != null && group[normalized] !== '') return group[normalized];
    for (const [k, v] of Object.entries(group)) {
      if (isIpeLayerName(k) && normalizeIpeLayerName(k) === normalized && v != null && v !== '') {
        return v;
      }
    }
  }
  return null;
}

function resolveExampleImageIdFromRef(rawRef) {
  if (rawRef == null) return undefined;
  if (typeof rawRef === 'object') {
    const id = String(rawRef.id || rawRef.imageId || '').trim();
    return id || undefined;
  }
  if (typeof rawRef === 'string') {
    if (
      rawRef.startsWith('data:')
      || rawRef.startsWith('http')
      || rawRef.startsWith('/')
      || rawRef.startsWith('blob:')
    ) {
      return undefined;
    }
    return rawRef.trim() || undefined;
  }
  return undefined;
}

/**
 * 将 imageGroup（印花区域名 → 图片 id/url）应用到 fabric JSON 中的印花位
 * @param {object} fabricJson
 * @param {Record<string, string>} imageGroup
 * @param {Record<string, string>} imageUrlMap
 */
export function applyStampImagesToFabricJson(fabricJson, imageGroup = {}, imageUrlMap = {}) {
  if (!fabricJson?.objects) return fabricJson;
  return {
    ...fabricJson,
    objects: fabricJson.objects.map((obj) => {
      if (!isStampFabricObject(obj)) return obj;
      const stampName = obj.linkData?.stampName || obj.name;
      const rawRef = lookupImageGroupRef(imageGroup, stampName, obj.name);
      if (!rawRef) return obj;
      const url = resolveImageRefToUrl(rawRef, imageUrlMap);
      if (!url) return obj;
      return {
        ...obj,
        linkData: mergeExampleIntoLinkData(
          {
            ...(obj.linkData || {}),
            role: 'stampArea',
            stampName: stampName || obj.linkData?.stampName,
          },
          {
            url,
            id: resolveExampleImageIdFromRef(rawRef),
          },
        ),
      };
    }),
  };
}

/**
 * 批量合成与二次编辑共用：同时写入 ipelayer 贴合图与 stampArea 印花图。
 * 仅替换 ipelayer 时，印花区域会仍显示模版示例图，选中素材无法落到成品上。
 */
export function applyCompositeImagesToFabricJson(fabricJson, imageGroup = {}, imageUrlMap = {}) {
  return applyStampImagesToFabricJson(
    applyImagesToFabricJson(fabricJson, imageGroup, imageUrlMap),
    imageGroup,
    imageUrlMap,
  );
}

/** 批量更新 fabric JSON 中所有印花区域的参考印花 */
export function patchExampleInFabricJson(fabricJson, example) {
  if (!fabricJson?.objects) return fabricJson;
  return {
    ...fabricJson,
    objects: fabricJson.objects.map((obj) => {
      if (!isStampFabricObject(obj)) return obj;
      return {
        ...obj,
        linkData: mergeExampleIntoLinkData(obj.linkData || { role: 'stampArea' }, example),
      };
    }),
  };
}

/**
 * 从 fabric 画布 JSON 导出 stampAreas
 * @param {object} fabricJson
 * @param {string} imageId
 */
export function exportStampAreasFromFabricJson(fabricJson, imageId) {
  if (!fabricJson?.objects) return { [imageId]: [] };
  const workspace = fabricJson.objects.find((o) => o.id === 'workspace');
  if (!workspace?.width || !workspace.height) return { [imageId]: [] };

  const workspaceSize = { width: workspace.width, height: workspace.height };
  const stamps = fabricJson.objects
    .filter((obj) => obj.id !== 'workspace' && (obj.linkData?.role === 'stampArea' || obj.stampRole === 'stampArea'))
    .map((obj, idx) => {
      const wsLeft = workspace.left || 0;
      const wsTop = workspace.top || 0;
      const frame = getStampFrameFromFabricJson(obj);
      let bounds;
      if (obj.type === 'group' && frame) {
        bounds = {
          left: (obj.left || 0) - wsLeft + (frame.left || 0),
          top: (obj.top || 0) - wsTop + (frame.top || 0),
          width: (frame.width || 0) * (frame.scaleX || 1),
          height: (frame.height || 0) * (frame.scaleY || 1),
          angle: frame.angle || obj.angle || 0,
        };
      } else {
        bounds = {
          left: (obj.left || 0) - wsLeft,
          top: (obj.top || 0) - wsTop,
          width: (obj.width || 0) * (obj.scaleX || 1),
          height: (obj.height || 0) * (obj.scaleY || 1),
          angle: obj.angle || 0,
        };
      }
      const mapped = workspaceBoundsToStampArea(bounds, workspaceSize);
      const params = stampParamsToExportFields(obj.linkData || {});
      const example = stampExampleFromLinkData(obj.linkData || {});
      return {
        id: obj.id || `stamp-${imageId}-${idx}`,
        name: obj.linkData?.stampName || obj.stampName || defaultStampAreaName(idx + 1),
        x: mapped.x,
        y: mapped.y,
        width: mapped.width,
        height: mapped.height,
        rotation: mapped.rotation,
        ...params,
        ...example,
      };
    });

  return { [imageId]: stamps };
}

/**
 * 将 stampAreas 合并进 fabric JSON（生成印花位矩形）
 */
export function injectStampAreasIntoFabricJson(fabricJson, imageId, stampAreasRaw) {
  const normalized = normalizeStampAreas(stampAreasRaw);
  const stamps = normalized[imageId] || [];
  if (!stamps.length || !fabricJson?.objects) return fabricJson;

  const workspace = fabricJson.objects.find((o) => o.id === 'workspace');
  if (!workspace) return fabricJson;

  const workspaceSize = { width: workspace.width, height: workspace.height };
  const stampObjects = stamps.map((stamp) => {
    const bounds = stampAreaToWorkspaceBounds(stamp, workspaceSize);
    const params = normalizeStampParams(stamp);
    const example = pickExampleExportFields(stamp);
    return buildStampLayerFabricObject(
      { ...stamp, ...params, ...example },
      workspace,
      bounds,
    );
  });

  const others = fabricJson.objects.filter(
    (o) => o.id === 'workspace' || !(o.linkData?.role === 'stampArea' || o.stampRole === 'stampArea'),
  );
  return {
    ...fabricJson,
    objects: [...others, ...stampObjects],
  };
}

/**
 * 从 Editor 实例导出 IPE 模板 payload 片段
 */
export function buildTemplatePayloadFromEditor(editor, meta = {}) {
  const rawJson = editor.getJson?.() || { objects: [] };
  const fabricJson = {
    ...rawJson,
    objects: (rawJson.objects || []).filter((obj) => obj.linkData?.role !== 'stampPreview'),
  };
  const imageId = meta.primaryImageId || meta.templateId || 'slide-1';
  const stampAreas = exportStampAreasFromFabricJson(fabricJson, imageId);

  return {
    fabricCanvas: fabricJson,
    primaryImageId: imageId,
    stampAreas,
    images: meta.images || [],
  };
}
